// URL/key vêm da fonte única (src/supabaseClient.js) — nada de ler env de
// Supabase direto em serviço/componente.
import {
  SUPABASE_URL,
  SUPABASE_PUBLISHABLE_KEY as SUPABASE_ANON_KEY,
} from '../supabaseClient';

/**
 * Faz parse da URL contida no QR code.
 *
 * Formatos suportados:
 *   Novo:    https://app.lesvita.com/ref/GYMCODE?join_token=UUID  (QR dinâmico)
 *   Legado:  https://app.lesvita.com/join?token=UUID&gym=GYMCODE  (formato anterior)
 *
 * @param {string} raw - URL lida pelo scanner de QR
 * @returns {{ tokenId: string, gymCode: string, teamCode: (string|null) }}
 */
export function parseQRUrl(raw) {
  try {
    const url = raw.startsWith('http')
      ? new URL(raw)
      : new URL(`https://app.lesvita.com${raw}`);

    // 1. join_token (novo formato) ou token (legado)
    const token = url.searchParams.get('join_token')
                || url.searchParams.get('token');

    // 2. gymCode: vem do path (/ref/GYMCODE) ou de query param (?gym= ou ?ref=)
    const pathParts   = url.pathname.split('/').filter(Boolean);
    const refIndex    = pathParts.indexOf('ref');
    const gymFromPath = refIndex >= 0 ? pathParts[refIndex + 1] : null;
    const gym         = gymFromPath
                      || url.searchParams.get('gym')
                      || url.searchParams.get('ref');

    // 3. teamCode (JOIN_CODE da equipe) — opcional, aditivo. Link de equipe:
    //    /ref/{gymCode}?team={JOIN_CODE}. Não é single-use (≠ join_token).
    //    O QR/pôster de equipe NÃO tem join_token — o vínculo (gym + equipe) é
    //    feito por applyGymReferralToUser, não por validate-qr-token. Por isso o
    //    token é opcional quando há team.
    const team = url.searchParams.get('team') || null;

    if (!gym)          throw new Error('Código da academia não encontrado no QR.');
    if (!token && !team) throw new Error('Token não encontrado no QR.');

    return { tokenId: token || null, gymCode: gym, teamCode: team };
  } catch (err) {
    throw new Error(
      err.message?.includes('Token') || err.message?.includes('academia')
        ? err.message
        : 'QR não reconhecido. Use o código da academia LesVita.'
    );
  }
}

/**
 * Chama a Edge Function validate-qr-token no Supabase.
 * Retorna { success, gym_code, premium_expires_at, premium_duration_days, is_renewal }
 *
 * @param {string} tokenId - UUID do token lido no QR
 * @param {string} firebaseUid - UID do usuário autenticado no Firebase
 */
export async function validateQRToken(tokenId, firebaseUid) {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
    throw new Error(
      'Variáveis de ambiente Supabase não encontradas ' +
      '(REACT_APP_SUPABASE_URL / REACT_APP_SUPABASE_PUBLISHABLE_KEY).'
    );
  }

  const res = await fetch(`${SUPABASE_URL}/functions/v1/validate-qr-token`, {
    method: 'POST',
    headers: {
      'Content-Type':  'application/json',
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({ token_id: tokenId, firebase_uid: firebaseUid }),
  });

  const data = await res.json();

  if (!res.ok || data.error) {
    const msgs = {
      'Token inválido':     'QR inválido. Peça à academia para mostrar o código atualizado.',
      'Token expirado':     'QR expirou. Aguarde o código se renovar na tela da academia.',
      'Token já utilizado': 'Este QR já foi usado. Aguarde o próximo código (30 s).',
    };
    throw new Error(msgs[data.error] ?? data.error ?? 'Erro ao validar. Tente novamente.');
  }

  return data;
}

/**
 * Calcula os dias restantes de premium.
 * @param {object|string} premiumExpiresAt - Firestore Timestamp ou string ISO
 * @returns {number|null}
 */
export function getDaysLeft(premiumExpiresAt) {
  if (!premiumExpiresAt) return null;
  const expiresDate = premiumExpiresAt?.toDate?.() ?? new Date(premiumExpiresAt);
  const diffMs      = expiresDate - new Date();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}